import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { MapPin, Store, CheckCircle2, XCircle, Loader2, Gift, CalendarDays, User, Search } from 'lucide-react';

interface PickupData {
  shop_name: string;
  shop_address: string;
  merchant_id?: number | null;
  pickup_code?: string;
  campaign_name: string;
  customer_initial: string;
  expires_at: number;
  expired: boolean;
  picked_up: boolean;
  picked_up_at: number;
}

// 谷友禮品到店兌換頁（/gf-pickup/:code）
// 客人視角：大 QR（內容＝店員核銷 URL，掃碼即開）＋兌換碼＋門店資訊
// 店員視角（?m=1）：掃客人 QR 進入 → 輸末 4 位核對 → 確認領取；無碼可人手輸入完整兌換碼查找
const GodfatherPickup: React.FC = () => {
  const params = useParams();
  const urlCode = params.code || '';
  const [data, setData] = useState<PickupData | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [staffInput, setStaffInput] = useState('');
  const [staffData, setStaffData] = useState<PickupData | null>(null);
  const [staffErr, setStaffErr] = useState<string | null>(null);
  const [staffLoading, setStaffLoading] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [shopOffers, setShopOffers] = useState<Array<{ key: string; title: string; imageUrl: string; validUntil: string }>>([]);
  const [redeemClaims, setRedeemClaims] = useState<Array<{ key: string; title: string; imageUrl: string; validUntil: string; redeemCode: string }>>([]);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemMsg, setRedeemMsg] = useState<string | null>(null);
  const [redeemOk, setRedeemOk] = useState(false);
  const staffMode = new URLSearchParams(window.location.search).get('m') === '1';
  const staffLookup = staffMode && !urlCode; // 店員人手輸入碼查找模式

  const fetchPickup = async (code: string) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/public/gf-pickup/${encodeURIComponent(code)}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) setErr(json.error || '兌換碼不存在');
      else setData(json.data ?? json);
    } catch {
      setErr('載入失敗，請重試');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (urlCode) fetchPickup(urlCode);
  }, [urlCode]);

  // T3：門店優惠即時展示（登記後開頁就見——推動即場消費；核銷前後都顯示）
  useEffect(() => {
    const mid = data?.merchant_id;
    if (!mid) return;
    let alive = true;
    fetch(`/api/public/godfather/merchant-offers/${mid}`)
      .then((r) => r.json().catch(() => ({})))
      .then((j) => { if (alive) setShopOffers(((j.data || {}).offers) || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [data?.merchant_id]);

  const staffLookupCode = async (fullCode: string) => {
    setStaffLoading(true);
    setStaffErr(null);
    setStaffData(null);
    try {
      const res = await fetch(`/api/public/gf-pickup/${encodeURIComponent(fullCode)}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) setStaffErr(json.error || '查無此碼');
      else {
        setStaffData(json.data ?? json);
      }
    } finally {
      setStaffLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (!verifyCode.trim()) { setRedeemMsg('請輸入門店驗證碼（4 位數字）'); return; }
    setRedeeming(true);
    setRedeemMsg(null);
    try {
      const code = staffLookup ? staffInput.trim().toUpperCase() : urlCode;
      const res = await fetch(`/api/public/gf-pickup/${encodeURIComponent(code)}/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verifyCode: verifyCode.trim() }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) setRedeemMsg(json.error || '核銷失敗');
      else {
        setRedeemOk(true);
        setRedeemClaims(json.merchantOffers || []);
        if (staffLookup) await staffLookupCode(staffInput.trim().toUpperCase());
        else await fetchPickup(urlCode);
      }
    } catch {
      setRedeemMsg('核銷失敗，請重試');
    } finally {
      setRedeeming(false);
    }
  };

  const expired = !!data?.expired;
  const picked = !!data?.picked_up;

  const statusBanner = (
    <>
      {picked ? (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
          <CheckCircle2 size={22} className="shrink-0 text-green-600" />
          <div>
            <p className="font-bold text-green-700 dark:text-green-300">已領取 ✓</p>
            <p className="text-xs text-green-600 dark:text-green-400">
              {data?.picked_up_at ? new Date(data.picked_up_at * 1000).toLocaleString('zh-HK') : ''}
            </p>
          </div>
        </div>
      ) : expired ? (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <XCircle size={22} className="shrink-0 text-red-600" />
          <p className="text-sm font-bold text-red-700 dark:text-red-300">此 QR 已過期（10 日有效期）——請聯絡客服重新安排</p>
        </div>
      ) : null}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-start sm:items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-red-500 to-red-600 px-5 py-4 text-white">
          <p className="text-xs text-red-100">{staffMode ? '店員核銷模式' : (data?.campaign_name || '香港辣谷 谷友禮遇')}</p>
          <h1 className="text-lg font-bold">🎁 谷友禮品到店兌換</h1>
        </div>

        <div className="p-5">
          {loading ? (
            <p className="py-10 text-center text-gray-400"><Loader2 size={26} className="mx-auto animate-spin" /></p>
          ) : staffLookup ? (
            /* 店員人手查找：輸完整兌換碼 */
            <div className="space-y-3">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">輸入客人嘅完整兌換碼（例如 GP6M6）</label>
              <div className="flex gap-2">
                <input
                  value={staffInput}
                  onChange={(e) => setStaffInput(e.target.value.toUpperCase())}
                  maxLength={8}
                  placeholder="GPXXXX"
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-2.5 font-mono text-lg tracking-widest outline-none focus:border-red-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                />
                <button onClick={() => staffInput.trim() && staffLookupCode(staffInput.trim().toUpperCase())} disabled={staffLoading} className="rounded-lg bg-red-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50">
                  {staffLoading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                </button>
              </div>
              {staffErr && <p className="text-xs text-red-600">{staffErr}</p>}
              {staffData && !staffErr && (
                <div className="mt-3 rounded-lg border border-gray-200 p-3 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-200">
                  <p className="flex items-center gap-2"><Gift size={14} /> {staffData.shop_name}</p>
                  <p className="mt-1 text-xs text-gray-400">{staffData.picked_up ? '此碼已領取' : '有效——可以核銷'}</p>
                </div>
              )}
            </div>
          ) : err ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              <XCircle size={22} className="mx-auto mb-2" />{err}
            </div>
          ) : data && (
            <>
              {statusBanner}

              {/* 客人視角：QR（內容＝店員核銷 URL） */}
              {!staffMode && !picked && !expired && (
                <div className="mb-4 flex flex-col items-center rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                  <QRCodeSVG value={`${window.location.origin}/gf-pickup/${encodeURIComponent(urlCode)}?m=1`} size={200} level="M" />
                  <p className="mt-3 font-mono text-lg font-bold tracking-widest text-gray-800 dark:text-white">{urlCode}</p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">到店出示此 QR 俾店員掃描</p>
                </div>
              )}

              {/* 門店資訊 */}
              <div className="mb-4 space-y-2 rounded-lg bg-gray-50 p-4 dark:bg-gray-900">
                <p className="flex items-center gap-2 text-sm font-bold text-gray-800 dark:text-gray-100"><Store size={15} className="text-red-600" /> {data.shop_name}</p>
                {data.shop_address && <p className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400"><MapPin size={13} className="mt-0.5 shrink-0" /> {data.shop_address}</p>}
                <p className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"><Gift size={13} /> {data.campaign_name}</p>
                {!picked && !expired && <p className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"><CalendarDays size={13} /> 有效期至 {data.expires_at ? new Date(data.expires_at * 1000).toLocaleDateString('zh-HK') : '—'}</p>}
              </div>

              {/* T3：本店優惠（閉環）——未核銷＝資訊卡推動到店；核銷後＝優惠券已自動入賬，即刻用得 */}
              {picked && redeemClaims.length > 0 && (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
                  <p className="mb-2 text-xs font-bold text-green-800 dark:text-green-300">
                    🎉 本店優惠已自動放入你個賬戶——即刻出示店員享用：
                  </p>
                  <div className="space-y-2.5">
                    {redeemClaims.map((o) => (
                      <div key={o.key} className="rounded-xl border-2 border-dashed border-green-300 bg-white p-3 dark:border-green-700 dark:bg-gray-800">
                        <div className="flex items-center gap-2.5">
                          {o.imageUrl && <img src={o.imageUrl} alt={o.title} className="h-11 w-11 shrink-0 rounded-lg object-cover" />}
                          <div className="min-w-0">
                            <p className="truncate text-xs font-bold text-gray-800 dark:text-gray-100">{o.title}</p>
                            <p className="font-mono text-sm font-black tracking-wider" style={{ color: '#d9381e' }}>{o.redeemCode}</p>
                            {o.validUntil && <p className="text-[10px] text-gray-500">至 {new Date(Number(o.validUntil) * 1000).toLocaleDateString('zh-HK')}</p>}
                          </div>
                        </div>
                        <a href={`/redeem/${encodeURIComponent(o.redeemCode)}`} target="_blank" rel="noreferrer"
                          className="mt-2 block rounded-lg py-2 text-center text-xs font-bold text-white" style={{ background: '#d9381e' }}>
                          📱 打開兌換頁出示 QR
                        </a>
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-[10px] text-green-700 dark:text-green-400">優惠已存入你嘅優惠中心，隨時喺「我的優惠」翻睇 ✅</p>
                </div>
              )}
              {!picked && shopOffers.length > 0 && (
                <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-800 dark:bg-orange-900/20">
                  <p className="mb-2 text-xs font-bold text-orange-800 dark:text-orange-300">
                    🎁 順便試埋呢間店嘅招牌優惠（到店出示即可）：
                  </p>
                  <div className="space-y-2">
                    {shopOffers.map((o) => (
                      <div key={o.key} className="flex items-center gap-2.5 rounded-lg bg-white p-2.5 dark:bg-gray-800">
                        {o.imageUrl && <img src={o.imageUrl} alt={o.title} className="h-11 w-11 shrink-0 rounded-lg object-cover" />}
                        <div className="min-w-0">
                          <p className="truncate text-xs font-bold text-gray-800 dark:text-gray-100">{o.title}</p>
                          {o.validUntil && <p className="text-[10px] text-gray-500">至 {o.validUntil}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 店員核銷確認（掃碼進入或人手查找後） */}
              {staffMode && !picked && !expired && (
                <div className="rounded-2xl border-2 border-red-200 dark:border-red-800 overflow-hidden">
                  <div className="bg-gradient-to-r from-red-500 to-red-600 px-5 py-3 text-white">
                    <p className="text-sm font-bold">核對後確認領取</p>
                  </div>
                  <div className="space-y-3 p-4">
                    <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                      <User size={15} className="text-gray-400" /> 客人：{data.customer_initial}氏
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                      <Gift size={15} className="text-gray-400" /> {data.campaign_name}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">店員核銷：輸入本店驗證碼（4 位數字）</label>
                      <div className="flex gap-2">
                        <input
                          value={verifyCode}
                          onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                          maxLength={4}
                          inputMode="numeric"
                          placeholder="如 1493"
                          className="flex-1 rounded-lg border border-gray-300 px-3 py-2.5 font-mono text-lg tracking-widest outline-none focus:border-red-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                        />
                        <button onClick={handleRedeem} disabled={redeeming || verifyCode.length < 4} className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50">
                          {redeeming ? <Loader2 size={16} className="animate-spin" /> : '確認領取'}
                        </button>
                      </div>
                      {redeemMsg && <p className="text-xs text-red-600">{redeemMsg}</p>}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="bg-gray-50 px-5 py-3 text-center text-xs text-gray-400 dark:bg-gray-900">
          香港🌶️辣谷 HKSpicyValley.com
        </div>
      </div>
    </div>
  );
};

export default GodfatherPickup;
